import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '@mantine/core';
import { IconArrowLeft, IconLock, IconPhone, IconShieldCheck } from '@tabler/icons-react';
import {
  packages,
  paymentMethods,
  TERMS_URL,
  SUPPORT_PHONE,
  withClientReference,
} from './paymentPackages';
import styles from './PaymentPage.module.css';
import './i18n'; // страница открывается напрямую по ссылке — инициализируем i18n явно

const track = (event, data) => {
  try {
    if (window.__track) window.__track(event, data);
  } catch (e) { /* ignore */ }
};

// Страница пополнения баланса: блок «Class packages» с сайта (PackagesSection),
// ссылки Stripe дополнены id ученика.
function PaymentPage() {
  const { t } = useTranslation();
  const { userId } = useParams();

  return (
    <div className={styles.page}>
      <Container size="xl">
        <div className={styles.topbar}>
          <img src="/logo.png" alt="LingvaPro" className={styles.logo} />
          <Link to={`/${userId}`} className={styles.back}>
            <IconArrowLeft size={18} stroke={1.75} aria-hidden />
            {t('backToDashboard')}
          </Link>
        </div>
      </Container>

      <section id="packages" className={styles.section}>
        <Container size="xl">
          <div className={styles.header}>
            <h2 className={styles.title}>{t('packages.title')}</h2>
            <p className={styles.subtitle}>{t('packages.subtitle')}</p>
          </div>

          <ul className={styles.grid}>
            {packages.map((item) => (
              <li
                key={item.id}
                className={`${styles.card} ${item.highlight ? styles.cardLead : ''}`}
              >
                {/* Язычок лидирующей карточки — над ней, снаружи, чтобы строки цен
                    во всех трёх карточках стояли на одном уровне */}
                {item.highlight && <p className={styles.ribbon}>{t('packages.best')}</p>}

                <div className={styles.cardHead}>
                  <h3 className={styles.classes}>{t('packages.classes', { count: item.classes })}</h3>
                </div>

                <p className={styles.price}>
                  {item.perClass}
                  <span className={styles.period}>{t('packages.period')}</span>
                </p>

                <p className={styles.meta}>
                  <span className={styles.total}>{t('packages.total', { price: item.price })}</span>
                  {item.save ? (
                    <span className={styles.save}>{t('packages.save', { percent: item.save })}</span>
                  ) : null}
                </p>

                <a
                  className={`${styles.cta} ${item.highlight ? styles.ctaLead : ''}`}
                  href={withClientReference(item.stripeUrl, userId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('packages.ctaAria', { classes: item.classes, price: item.price })}
                  onClick={() => track('buy_click', { package: item.id })}
                >
                  {t('packages.cta')}
                </a>
              </li>
            ))}
          </ul>

          <div className={styles.footer}>
            <aside className={styles.help}>
              <div>
                <p className={styles.helpTitle}>{t('packages.helpTitle')}</p>
                <p className={styles.helpText}>{t('packages.helpText')}</p>
              </div>
              <a
                className={styles.helpPhone}
                href={SUPPORT_PHONE.href}
                onClick={() => track('phone_click')}
              >
                <IconPhone size={20} stroke={1.75} aria-hidden />
                {SUPPORT_PHONE.label}
              </a>
            </aside>
          </div>

          <div className={styles.trust}>
            <p className={styles.guarantee}>
              <IconShieldCheck size={20} stroke={1.75} aria-hidden />
              <span>
                {t('packages.guarantee')}{' '}
                <a className={styles.guaranteeLink} href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                  {t('packages.guaranteeLink')}
                </a>
              </span>
            </p>

            <div className={styles.payment}>
              <p className={styles.secure}>
                <IconLock size={16} stroke={1.75} aria-hidden />
                {t('packages.secure')}
              </p>

              <ul className={styles.methods} aria-label={t('packages.accepted')}>
                {paymentMethods.map((method) => (
                  <li key={method.id} className={styles.method}>
                    <img src={method.file} alt={method.name} width={120} height={80} />
                  </li>
                ))}
              </ul>

              <p className={styles.installments}>{t('packages.installments')}</p>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default PaymentPage;
